import './secureStorageBootstrap';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './utils/validation';
import { ThemeProvider } from './context/ThemeContext';
import { initTheme } from './utils/themeService';
import { Provider } from "react-redux";
import reportWebVitals from './reportWebVitals';
import { store } from "./store";
import { setupCrossTabLogoutListener } from "./utils/crossTabAuthSync";
import BrandedLoader from "./components/BrandedLoader/BrandedLoader";
import { exposeAppVersionOnWindow, logAppVersion } from "./config/appVersion";
import lazyWithRetry from "./utils/lazyWithRetry";

const App = lazyWithRetry(() => import('./App'));
(function ensureGlobalThisPolyfill(root) {
  if (typeof globalThis === "undefined") {
    Object.defineProperty(root, 'globalThis', {
      value: root,
      writable: true,
      enumerable: false,
      configurable: true,
    });
  }
}(globalThis.window ?? globalThis.self ?? globalThis));

/** Recover from stale dev chunks after HMR/rebuild (ChunkLoadError on lazy routes). */
(function installChunkLoadRecovery() {
  const root = globalThis.window;
  if (!root) return;

  const reloadOnceForStaleChunk = () => {
    const key = "mytara:chunk-reload";
    if (root.sessionStorage?.getItem(key)) {
      root.sessionStorage.removeItem(key);
      return false;
    }
    root.sessionStorage?.setItem(key, "1");
    root.location.reload();
    return true;
  };

  const isChunkLoadFailure = (reason) => {
    const name = reason?.name ?? "";
    const message = String(reason?.message ?? reason ?? "");
    return (
      name === "ChunkLoadError" ||
      message.includes("Loading chunk") ||
      message.includes("ChunkLoadError")
    );
  };

  root.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadFailure(event.reason)) return;
    if (reloadOnceForStaleChunk()) {
      event.preventDefault();
    }
  });

  root.addEventListener("error", (event) => {
    if (!isChunkLoadFailure(event.error ?? event.message)) return;
    if (reloadOnceForStaleChunk()) {
      event.preventDefault();
    }
  });
}(globalThis.window ?? globalThis.self ?? globalThis));

/* After App (antd) bundle order: keep Input.Search clear visible when text exists */


/**
 * sessionStorage is per-tab in the browser.
 * User request: "session se kro" meaning "keep session data while opening a new tab".
 *
 * We simulate shared session across tabs by:
 *  - When a tab starts, it asks other open tabs for a snapshot of known sessionStorage keys.
 *  - Other tabs respond with their snapshot.
 *  - The new tab applies those keys to its own sessionStorage before React renders.
 */
async function syncSessionStorageFromOtherTab() {
  try {
    if (!globalThis?.window) return;
    if (!globalThis?.BroadcastChannel) return;
    if (!globalThis.window.sessionStorage) return;

    const CHANNEL_NAME = "drug-sync:session-storage-sync";
    const SYNC_KEYS = [
      "token",
      "userData",
      "familyMembers",
      "roles",
      "activeProfileContext",
      "pendingRegisterToast",
    ];

    // This tab already has a session — skip waiting on other tabs.
    if (globalThis.window.sessionStorage.getItem("token")) {
      return;
    }

    // Create a stable tab identifier for this page load.
    const tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`; // NOSONAR - transient session-sync tab identifier, no security boundary

    const channel = new BroadcastChannel(CHANNEL_NAME);

    const readSnapshot = () => {
      const snap = {};
      for (const key of SYNC_KEYS) {
        const v = globalThis.window.sessionStorage.getItem(key);
        if (v !== null) snap[key] = v;
      }
      return snap;
    };

    const applySnapshot = (snapshot) => {
      if (!snapshot || typeof snapshot !== "object") return;
      // Merge only — do not delete keys missing from another tab's snapshot (fixes blank dashboard on reload).
      for (const key of SYNC_KEYS) {
        if (!Object.hasOwn(snapshot, key)) continue;
        const value = snapshot[key];
        if (value === null || value === undefined || value === "") {
          continue;
        }
        globalThis.window.sessionStorage.setItem(key, String(value));
      }
    };

    // Respond to other tabs asking for a session snapshot.
    const onMessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.tabId === tabId) return;

      if (msg.type === "REQUEST_SESSION_SNAPSHOT") {
        channel.postMessage({
          type: "SESSION_SNAPSHOT",
          tabId,
          snapshot: readSnapshot(),
        });
      }
    };

    channel.addEventListener("message", onMessage);

    // Ask other tabs for their current session snapshot.
    const snapshot = await new Promise((resolve) => {
      let resolved = false;

      const MAX_WAIT_MS = 400;
      const startAt = Date.now();
      let timeout;

      const handleSnapshot = (event) => {
        const msg = event?.data;
        if (msg?.type !== "SESSION_SNAPSHOT") return;
        if (msg?.tabId === tabId) return;

        if (resolved) return;
        resolved = true;
        globalThis.window.clearTimeout(timeout);
        channel.removeEventListener("message", handleSnapshot);
        resolve(msg.snapshot || null);
      };

      channel.addEventListener("message", handleSnapshot);

      const sendRequest = () => {
        if (resolved) return;
        channel.postMessage({ type: "REQUEST_SESSION_SNAPSHOT", tabId });
      };

      // Send immediately + periodic retries until snapshot arrives.
      sendRequest();

      const interval = globalThis.window.setInterval(() => {
        if (resolved) {
          globalThis.window.clearInterval(interval);
          return;
        }
        if (Date.now() - startAt > MAX_WAIT_MS) {
          globalThis.window.clearInterval(interval);
          return;
        }
        sendRequest();
      }, 250);

      timeout = globalThis.window.setTimeout(() => {
        if (resolved) return;
        resolved = true;
        globalThis.window.clearInterval(interval);
        channel.removeEventListener("message", handleSnapshot);
        resolve(null);
      }, MAX_WAIT_MS);

      // Best-effort cleanup
      
    });

    applySnapshot(snapshot);

    // Keep the channel open (so other future tabs can still sync).
    // No close() here.
  } catch {
    // Never block app render due to sync issues.
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Cross-tab session sync must not block first paint (App rehydrates auth in useEffect).
await syncSessionStorageFromOtherTab();

// Ensure logout in one tab logs out all tabs.
setupCrossTabLogoutListener(store.dispatch);

initTheme();
exposeAppVersionOnWindow();
logAppVersion();

root.render(
  <Provider store={store}>
    <ThemeProvider>
      <Suspense fallback={<BrandedLoader fullscreen message="Loading" />}>
        <App />
      </Suspense>
    </ThemeProvider>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
if (typeof globalThis.requestIdleCallback === "function") {
  globalThis.requestIdleCallback(() => reportWebVitals());
} else {
  globalThis.setTimeout(() => reportWebVitals(), 1);
}
